/**
 * Doctor Model (Data Layer)
 * Manages all direct Firestore transactions for the Doctor, Sales, and Investment collections.
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  onSnapshot 
} from 'firebase/firestore';
import { db, auth } from '../firebase.js';
import { writeAuditLog } from './auditModel.js';
import { currentUserRole } from '../auth.js';

const DOCTORS_COLL = 'doctors';
const SALES_COLL = 'sales';
const INVESTMENTS_COLL = 'investments';

/**
 * Register a new Doctor CRM profile
 * @param {object} doctorData 
 * @returns {Promise<string>} - New Document ID
 */
export async function addDoctor(doctorData) {
  const payload = {
    name: doctorData.name,
    specialization: doctorData.specialization || 'General Medicine',
    area: doctorData.area || '',
    city: doctorData.city || '',
    hospital: doctorData.hospital || '',
    phone: doctorData.phone || '',
    remarks: doctorData.remarks || '',
    currentPrescribedBrand: doctorData.currentPrescribedBrand || '',
    targetBrands: doctorData.targetBrands || [],
    email: doctorData.email || '',
    dob: doctorData.dob || '',
    anniversary: doctorData.anniversary || '',
    status: currentUserRole === 'Admin' ? 'Approved' : 'Pending_Create',
    createdBy: auth.currentUser ? auth.currentUser.uid : 'system',
    createdByEmail: auth.currentUser ? auth.currentUser.email : 'system@aurapharma.com',
    createdDate: new Date().toISOString(),
    updatedDate: new Date().toISOString()
  };

  const docRef = await addDoc(collection(db, DOCTORS_COLL), payload);
  
  // Write Audit Log (background)
  writeAuditLog('CREATE', 'doctors', docRef.id, payload);
  
  return docRef.id;
}

/**
 * Update an existing Doctor profile properties
 * @param {string} id 
 * @param {object} doctorData 
 * @returns {Promise<void>}
 */
export async function updateDoctor(id, doctorData) {
  const docRef = doc(db, DOCTORS_COLL, id);
  const payload = {
    name: doctorData.name,
    specialization: doctorData.specialization,
    area: doctorData.area,
    city: doctorData.city,
    hospital: doctorData.hospital,
    phone: doctorData.phone,
    remarks: doctorData.remarks,
    currentPrescribedBrand: doctorData.currentPrescribedBrand || '',
    targetBrands: doctorData.targetBrands || [],
    email: doctorData.email || '',
    dob: doctorData.dob || '',
    anniversary: doctorData.anniversary || '',
    status: doctorData.status || 'Approved',
    updatedDate: new Date().toISOString()
  };
  
  const result = await updateDoc(docRef, payload);
  // Write Audit Log (background)
  writeAuditLog('UPDATE', 'doctors', id, payload);
  return result;
}

/**
 * Delete a Doctor profile and their related transactional records
 * @param {string} id 
 * @returns {Promise<void>}
 */
export async function deleteDoctor(id) {
  // Write Audit Log first before cascading deletion (background)
  writeAuditLog('DELETE', 'doctors', id, { note: "Purged profile and cascade deleted logs" });

  // 1. Delete doctor doc
  const mainDelete = deleteDoc(doc(db, DOCTORS_COLL, id));

  // 2. Cascade delete related sales (background)
  const salesQuery = query(collection(db, SALES_COLL), where('doctorId', '==', id));
  getDocs(salesQuery).then((salesDocs) => {
    salesDocs.forEach((d) => {
      deleteDoc(doc(db, SALES_COLL, d.id));
    });
  }).catch(e => console.error("Cascade sales delete query error:", e));

  // 3. Cascade delete related investments (background)
  const investQuery = query(collection(db, INVESTMENTS_COLL), where('doctorId', '==', id));
  getDocs(investQuery).then((investDocs) => {
    investDocs.forEach((d) => {
      deleteDoc(doc(db, INVESTMENTS_COLL, d.id));
    });
  }).catch(e => console.error("Cascade investments delete query error:", e));

  return mainDelete;
}

/**
 * Fetch a single Doctor document by ID
 * @param {string} id 
 * @returns {Promise<object>}
 */
export async function getDoctor(id) {
  const docRef = doc(db, DOCTORS_COLL, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  } else {
    throw new Error("Doctor profile not found.");
  }
}

/**
 * Set up real-time listener for the Doctors collection
 * @param {function} callback - callback triggered with doctors array
 * @returns {import('firebase/firestore').Unsubscribe}
 */
export function listenToDoctors(callback) {
  const collRef = collection(db, DOCTORS_COLL);
  let q;
  if (currentUserRole === 'Representative' && auth.currentUser) {
    q = query(collRef, where('createdBy', '==', auth.currentUser.uid));
  } else {
    q = query(collRef, orderBy('name', 'asc'));
  }
  
  return onSnapshot(q, (snapshot) => {
    const doctors = [];
    snapshot.forEach((doc) => {
      doctors.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort in memory by name if representative
    if (currentUserRole === 'Representative') {
      doctors.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    
    callback(doctors);
  }, (error) => {
    console.error("Firestore onSnapshot error:", error);
  });
}

/**
 * Get Sales History associated with a specific Doctor
 * @param {string} doctorId 
 * @returns {Promise<Array>}
 */
export async function getDoctorSales(doctorId) {
  const q = query(
    collection(db, SALES_COLL), 
    where('doctorId', '==', doctorId)
  );
  const querySnapshot = await getDocs(q);
  const sales = [];
  querySnapshot.forEach((doc) => {
    sales.push({ id: doc.id, ...doc.data() });
  });
  // Sort in memory by date descending to avoid requiring composite indexes
  sales.sort((a, b) => new Date(b.date) - new Date(a.date));
  return sales;
}

/**
 * Get Investment History associated with a specific Doctor
 * @param {string} doctorId 
 * @returns {Promise<Array>}
 */
export async function getDoctorInvestments(doctorId) {
  const q = query(
    collection(db, INVESTMENTS_COLL), 
    where('doctorId', '==', doctorId)
  );
  const querySnapshot = await getDocs(q);
  const investments = [];
  querySnapshot.forEach((doc) => {
    investments.push({ id: doc.id, ...doc.data() });
  });
  // Sort in memory by date descending to avoid requiring composite indexes
  investments.sort((a, b) => new Date(b.date) - new Date(a.date));
  return investments;
}

/**
 * Seed randomized sales and investment records for interactive visual dashboards
 * @param {string} doctorId 
 * @param {string} docName 
 */
async function seedMockRecordsForDoctor(doctorId, docName) {
  const salesSeeds = [
    { medicineName: 'Metformin Hydrochloride 500mg', qty: 100, price: 49.00, monthsAgo: 4 },
    { medicineName: 'Atorvastatin Calcium 10mg', qty: 150, price: 45.00, monthsAgo: 3 },
    { medicineName: 'Amoxicillin Trihydrate 250mg', qty: 80, price: 29.00, monthsAgo: 2 },
    { medicineName: 'Metformin Hydrochloride 500mg', qty: 120, price: 49.00, monthsAgo: 1 },
    { medicineName: 'Lisinopril 10mg', qty: 200, price: 35.00, monthsAgo: 0 }
  ];

  const investmentSeeds = [
    { campaignName: 'Cardiology Seminar Sponsorship', amount: 2500, monthsAgo: 4, category: 'Seminar' },
    { campaignName: 'Free Medicine Samples Distribution', amount: 800, monthsAgo: 2, category: 'Free Samples' },
    { campaignName: 'Medical Journal Subscriptions', amount: 400, monthsAgo: 1, category: 'Literature' }
  ];

  // Seed sales
  for (const s of salesSeeds) {
    const date = new Date();
    date.setMonth(date.getMonth() - s.monthsAgo);
    await addDoc(collection(db, SALES_COLL), {
      doctorId: doctorId,
      doctorName: docName,
      medicineName: s.medicineName,
      quantity: s.qty,
      unitPrice: s.price,
      amount: s.qty * s.price,
      date: date.toISOString()
    });
  }

  // Seed investments
  for (const i of investmentSeeds) {
    const date = new Date();
    date.setMonth(date.getMonth() - i.monthsAgo);
    await addDoc(collection(db, INVESTMENTS_COLL), {
      doctorId: doctorId,
      doctorName: docName,
      campaignName: i.campaignName,
      amount: i.amount,
      category: i.category,
      date: date.toISOString()
    });
  }
}

/**
 * Tags doctor's location coordinates and human-readable place name
 * @param {string} doctorId 
 * @param {number} latitude 
 * @param {number} longitude 
 * @param {string} locationName 
 */
export async function tagDoctorLocation(doctorId, latitude, longitude, locationName) {
  const docRef = doc(db, DOCTORS_COLL, doctorId);
  const payload = {
    latitude,
    longitude,
    locationName,
    updatedDate: new Date().toISOString()
  };
  
  await updateDoc(docRef, payload);
  writeAuditLog('UPDATE_LOCATION', 'doctors', doctorId, payload);
}

